import React, { useEffect, useState } from 'react';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';

const PAGE_SIZES = [10, 20, 50, 100];

// What the log can hold in its pii_field column (the mobile number of a message / of a user consent / of a user interest, the message's request and
// response, the attachment link) and in its action column - the two filters that are a pick from a list, not free text.
const PII_FIELDS = [
  'toWAUserPhoneNumber',
  'userConsentMobileNumber',
  'userInterestMobileNumber',
  'waRequest',
  'waExternalRequest',
  'waResponse',
  'fileStorageObjectKey',
];
const ACTIONS = [
  { value: 'viewed', label: 'Viewed' },
  { value: 'downloaded', label: 'Downloaded' },
];

const INITIAL_FILTERS = { piiField: '', wabaId: '', userId: '', action: '', notificationId: '', userConsentId: '', userInterestId: '' };

/**
 * Compliance Audit Log - a read-only view of the service's PII access log: every time a SYSTEM_ADMIN / ADMIN unmasked a
 * personal-data field or downloaded an attachment (and every refused attempt), newest first. A filter section, headers,
 * rows and paging only - nothing here can change the log. SYSTEM_ADMIN only; the service enforces the same rule on the call
 * itself.
 */
const WaPiiAuditLogPage = () => {

  const waApi = useWaApi();
  const { canViewPiiAuditLog, loaded } = useUserProfileAccess();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // What the user is typing, versus what the log was last filtered by: typing alone does not search.
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

  useEffect(() => {
    if (!loaded || !canViewPiiAuditLog) {
      return undefined;
    }
    let cancelled = false;
    const fetchLog = async () => {
      setLoading(true);
      try {
        // Only the filters that were filled in are sent.
        const params = { page, size };
        Object.entries(appliedFilters).forEach(([key, value]) => {
          const trimmed = String(value).trim();
          if (trimmed !== '') {
            params[key] = trimmed;
          }
        });
        const response = await waApi.get(process.env.REACT_APP_GET_WA_PII_AUDIT_LOG_BY_PAGE_ENDPOINT, { params });
        if (cancelled) return;
        const { message, object, httpStatus } = response.data;
        if (httpStatus === 200 && object) {
          setRows(object.content ?? []);
          setTotalPages(object.totalPages ?? 0);
          setTotalElements(object.totalElements ?? 0);
          setError(null);
        } else {
          setError(message || 'Unable to load the PII access log.');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Unable to load the PII access log.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLog();
    return () => { cancelled = true; };
  }, [waApi, page, size, appliedFilters, loaded, canViewPiiAuditLog]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // A consent / interest id is a number: anything else is dropped as it is typed, rather than refused by the service later.
    setFilters((prev) => ({ ...prev, [name]: name === 'userConsentId' || name === 'userInterestId' ? value.replace(/\D/g, '') : value }));
  };

  const applyFilters = () => {
    setPage(0);
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    setPage(0);
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
  };

  const hasAppliedFilters = Object.values(appliedFilters).some((value) => String(value).trim() !== '');

  const formatTime = (value) => (value ? new Date(value).toLocaleString() : '-');
  const orDash = (value) => (value === null || value === undefined || value === '' ? '-' : value);

  if (!loaded) {
    return <div className="page-container">Loading...</div>;
  }

  if (!canViewPiiAuditLog) {
    return (
      <div className="page-container">
        <h1>Compliance Audit Log</h1>
        <div className="profile-empty">Only a SYSTEM_ADMIN can view the compliance audit log.</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>Compliance Audit Log</h1>
      <p className="audit-subtitle">
        Every time personal data was unmasked or an attachment was downloaded - and every attempt that was refused.
        Newest first. Read only.
      </p>

      <div className="flex-row filter-section">
        <div className="filter-item">
          <label htmlFor="audit-items-per-page">Items per page: </label>
          <select
            id="audit-items-per-page"
            className="w-auto"
            value={size}
            onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          >
            {PAGE_SIZES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <h3>Filters</h3>
      <form
        className="flex-row filter-section"
        onSubmit={(e) => { e.preventDefault(); applyFilters(); }}
      >
        <div className="filter-item">
          <label htmlFor="audit-filter-pii-field">PII Field:</label>
          <select id="audit-filter-pii-field" name="piiField" value={filters.piiField} onChange={handleInputChange}>
            <option value="">All</option>
            {PII_FIELDS.map((field) => (
              <option key={field} value={field}>{field}</option>
            ))}
          </select>
        </div>
        <div className="filter-item">
          <label htmlFor="audit-filter-waba">Business Account ID:</label>
          <input id="audit-filter-waba" type="text" name="wabaId" value={filters.wabaId} onChange={handleInputChange} />
        </div>
        <div className="filter-item">
          <label htmlFor="audit-filter-user">User ID:</label>
          <input id="audit-filter-user" type="text" name="userId" value={filters.userId} onChange={handleInputChange} />
        </div>
        <div className="filter-item">
          <label htmlFor="audit-filter-action">Action:</label>
          <select id="audit-filter-action" name="action" value={filters.action} onChange={handleInputChange}>
            <option value="">All</option>
            {ACTIONS.map((action) => (
              <option key={action.value} value={action.value}>{action.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-item">
          <label htmlFor="audit-filter-notification">Notification ID:</label>
          <input id="audit-filter-notification" type="text" name="notificationId" value={filters.notificationId} onChange={handleInputChange} />
        </div>
        <div className="filter-item">
          <label htmlFor="audit-filter-consent">Consent ID:</label>
          <input
            id="audit-filter-consent"
            type="text"
            inputMode="numeric"
            name="userConsentId"
            value={filters.userConsentId}
            onChange={handleInputChange}
            placeholder="Only numeric value"
          />
        </div>
        <div className="filter-item">
          <label htmlFor="audit-filter-interest">Interest ID:</label>
          <input
            id="audit-filter-interest"
            type="text"
            inputMode="numeric"
            name="userInterestId"
            value={filters.userInterestId}
            onChange={handleInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <button type="submit">Apply Filters</button>
        <button type="button" onClick={resetFilters}>Reset Filters</button>
      </form>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {error}
        </div>
      )}

      <div className="table-responsive">
        <table className="notifications-table">
          <thead>
            <tr>
              <th>Id</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>User Role</th>
              <th>User ID</th>
              <th>Tenant ID</th>
              <th>Business Account ID</th>
              <th>PII Field</th>
              <th>Action</th>
              <th>Result</th>
              <th>IP Address</th>
              <th>Notification ID</th>
              <th>Consent ID</th>
              <th>Interest ID</th>
              <th>Request ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{formatTime(row.createdDate)}</td>
                <td>{formatTime(row.updatedDate)}</td>
                <td>{orDash(row.userRole)}</td>
                <td className="audit-mono">{orDash(row.userId)}</td>
                <td className="audit-mono">{orDash(row.tenantId)}</td>
                <td className="audit-mono">{orDash(row.wabaId)}</td>
                <td>{orDash(row.piiField)}</td>
                <td><span className="audit-badge action">{orDash(row.action)}</span></td>
                <td><span className={`audit-badge ${row.result === 'success' ? 'success' : 'failure'}`}>{orDash(row.result)}</span></td>
                <td className="audit-mono">{orDash(row.ipAddress)}</td>
                <td className="audit-mono audit-wrap">{orDash(row.notificationId)}</td>
                <td className="audit-mono">{orDash(row.userConsentId)}</td>
                <td className="audit-mono">{orDash(row.userInterestId)}</td>
                <td className="audit-mono audit-wrap">{orDash(row.requestId)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && !error && (
          <div className="audit-empty">
            {hasAppliedFilters ? 'No access events match these filters.' : 'No access events have been recorded yet.'}
          </div>
        )}
        {loading && <div className="audit-empty">Loading...</div>}
      </div>

      <div className="pagination-container">
        <button type="button" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0 || loading}>Previous</button>
        <span>Page {totalPages === 0 ? 0 : page + 1} of {totalPages} ({totalElements} records)</span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page + 1 >= totalPages || loading}>Next</button>
      </div>
    </div>
  );
};

export default WaPiiAuditLogPage;
